/* =============================================================================
   SCENE KIT — the reusable half of a cinematic scroll-driven WebGL page.

   Everything in here is world-agnostic: it does not know whether you are
   building a temple, a market hall, a foundry or a glacier. It gives you the
   parts that are the same every time, so that the only code you actually write
   for a new project is the world itself and the words on top of it.

     SceneKit.rng / noise2D / fbm / lerp / damp …   deterministic maths
     SceneKit.bake*                                 the texture kitchen
     SceneKit.Batch                                 merge geometry per material
     SceneKit.mat / tiled / gBox / gCyl / place     material and primitive help
     SceneKit.createRenderer                        an HDR-first renderer
     SceneKit.Composer                              bright pass → bloom → ACES
     SceneKit.Rig                                   scroll-driven camera path
     SceneKit.dust                                  drifting motes, GPU-animated
     SceneKit.shaftVolume                           visible light through an opening
     SceneKit.page                                  reveals, rail, nav, fallback

   Requires THREE (r149 tested) on the global before it loads. No modules, no
   build step: it is a plain script so it can be dropped into a Shopify asset,
   a static page or a bundler without ceremony.

   Class convention: every element the page half touches is `cs-*`, scoped
   inside `#cs-root`. chapters.css and page-template.html use the same names,
   so the three files fit together without any wiring.
   ========================================================================== */
(function (global) {
'use strict';
var THREE = global.THREE;
var K = { version: '1.0.0', maxAniso: 1 };

var Q  = new URLSearchParams(location.search);
/* Grade and camera knobs are readable from the URL (?cs-exp=, ?cs-bloom=…).
   Tuning a look is a hundred small guesses; making each one a page reload
   rather than a redeploy is the difference between an hour and a day. */
K.qn = function (k, d) { var v = Q.get('cs-' + k); return v === null ? d : parseFloat(v); };
K.REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;
K.COARSE = matchMedia('(hover: none)').matches;
K.FINE   = matchMedia('(hover: hover) and (pointer: fine)').matches;


/* ============================================== 1 · maths and noise ======= */
var PI    = Math.PI, TAU = PI * 2;
var clamp = function (v, a, b) { return v < a ? a : (v > b ? b : v); };
var sat   = function (v) { return clamp(v, 0, 1); };
var lerp  = function (a, b, t) { return a + (b - a) * t; };
var smoothstep = function (e0, e1, x) { var t = sat((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); };
var damp  = function (cur, to, rate, dt) { return lerp(cur, to, 1 - Math.exp(-rate * dt)); };

/* mulberry32 — small, fast, and seedable, so the hall is the same hall on
   every load and in every browser. Nothing here may use Math.random(). */
function rng(seed) {
  var a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    var t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function noise2D(seed) {
  var r = rng(seed), p = new Uint8Array(256), perm = new Uint8Array(512), i, j, t;
  for (i = 0; i < 256; i++) p[i] = i;
  for (i = 255; i > 0; i--) { j = (r() * (i + 1)) | 0; t = p[i]; p[i] = p[j]; p[j] = t; }
  for (i = 0; i < 512; i++) perm[i] = p[i & 255];
  var G = [[1,1],[-1,1],[1,-1],[-1,-1],[1,0],[-1,0],[0,1],[0,-1]];
  var fade = function (t) { return t * t * t * (t * (t * 6 - 15) + 10); };
  return function (x, y) {
    var xi = Math.floor(x), yi = Math.floor(y);
    var X = xi & 255, Y = yi & 255, xf = x - xi, yf = y - yi;
    var u = fade(xf), v = fade(yf);
    var g = function (h, dx, dy) { var q = G[h & 7]; return q[0] * dx + q[1] * dy; };
    var aa = perm[perm[X] + Y], ab = perm[perm[X] + Y + 1];
    var ba = perm[perm[X + 1] + Y], bb = perm[perm[X + 1] + Y + 1];
    var x1 = lerp(g(aa, xf, yf), g(ba, xf - 1, yf), u);
    var x2 = lerp(g(ab, xf, yf - 1), g(bb, xf - 1, yf - 1), u);
    return lerp(x1, x2, v);
  };
}
function fbm(n, x, y, oct, lac, gain) {
  var v = 0, a = .5, f = 1, i;
  for (i = 0; i < oct; i++) { v += a * n(x * f, y * f); f *= (lac || 2); a *= (gain || .5); }
  return v;
}
function cvs(w, h) { var c = document.createElement('canvas'); c.width = w; c.height = h; return c; }
function rgb(r, g, b) { return 'rgb(' + (r | 0) + ',' + (g | 0) + ',' + (b | 0) + ')'; }
function rgba(r, g, b, a) { return 'rgba(' + (r | 0) + ',' + (g | 0) + ',' + (b | 0) + ',' + a + ')'; }

/* Vertex colours and light intensities are consumed in LINEAR space, so every
   palette entry is written the way a designer picks it — in sRGB — and passed
   through here once. Skipping this is why hand-picked colours come out washed. */
function lin(h) {
  var r = ((h >> 16) & 255) / 255, g = ((h >> 8) & 255) / 255, b = (h & 255) / 255;
  var f = function (v) { return v <= .04045 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4); };
  return [f(r), f(g), f(b)];
}


/* ============================================== 2 · the texture kitchen === */
/* Every surface in the hall is painted here, in a 2D canvas, at boot. Nothing
   is downloaded. Each recipe returns { map, normalMap, roughnessMap } so the
   material builders below stay one line long, and every canvas is thrown away
   as soon as Three has uploaded it.

   Height → normal is done once, in JS, rather than leaning on a bump map:
   a real normal map is what makes plaster read as plaster under a raking sun
   at four in the afternoon, and the sun in this hall is always raking. */

function normalFromHeight(hc, strength) {
  var W = hc.width, H = hc.height;
  var b = cvs(W, H), bx = b.getContext('2d');
  bx.drawImage(hc, 0, 0);
  var src = bx.getImageData(0, 0, W, H).data;
  var out = cvs(W, H), ox = out.getContext('2d');
  var im = ox.createImageData(W, H), d = im.data;
  var at = function (x, y) { return src[((((y % H) + H) % H) * W + (((x % W) + W) % W)) * 4] / 255; };
  var s = strength || 2.2, x, y, gx, gy, nx, ny, nz, il, i;
  for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
    gx = (at(x + 1, y) - at(x - 1, y)) * s;
    gy = (at(x, y + 1) - at(x, y - 1)) * s;
    nx = -gx; ny = -gy; nz = 1;
    il = 1 / Math.sqrt(nx * nx + ny * ny + 1);
    i = (y * W + x) * 4;
    d[i]     = (nx * il * .5 + .5) * 255;
    d[i + 1] = (ny * il * .5 + .5) * 255;
    d[i + 2] = (nz * il * .5 + .5) * 255;
    d[i + 3] = 255;
  }
  ox.putImageData(im, 0, 0);
  return out;
}

var TEXCACHE = {};
function toTex(canvas, rx, ry, srgb) {
  var t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(rx || 1, ry || 1);
  t.anisotropy = K.maxAniso;
  if (srgb) t.encoding = THREE.sRGBEncoding;
  t.needsUpdate = true;
  return t;
}
/* A recipe is only ever cooked once even if six builders ask for it. */
function recipe(key, fn) {
  if (!TEXCACHE[key]) TEXCACHE[key] = fn();
  return TEXCACHE[key];
}

/* — lime-washed plaster: the hall's walls ------------------------------- */
function bakePlaster(seed, tint) {
  var W = 1024, H = 1024;
  var c = cvs(W, H), x = c.getContext('2d');
  var h = cvs(W, H), hx = h.getContext('2d');
  var r = cvs(W, H), rx = r.getContext('2d');
  var n = noise2D(seed), n2 = noise2D(seed + 411), r0 = rng(seed + 9);
  var base = tint || [214, 194, 162];

  var im = x.createImageData(W, H), d = im.data;
  var hi = hx.createImageData(W, H), hd = hi.data;
  var ri = rx.createImageData(W, H), rd = ri.data;
  var px, py, v, w, t, i, tone, rough;
  for (py = 0; py < H; py++) for (px = 0; px < W; px++) {
    i = (py * W + px) * 4;
    /* three scales: the trowel sweep, the lime mottle, the grit */
    v = fbm(n, px / 210, py / 210, 4, 2.1, .52);
    w = fbm(n2, px / 34, py / 34, 3, 2.4, .48);
    t = .84 + v * .30 + w * .10 + (r0() - .5) * .05;
    d[i]     = base[0] * t;
    d[i + 1] = base[1] * t * (1 + v * .03);
    d[i + 2] = base[2] * t * (1 - v * .05);
    d[i + 3] = 255;
    tone = 128 + v * 78 + w * 40;
    hd[i] = hd[i + 1] = hd[i + 2] = tone; hd[i + 3] = 255;
    /* plaster is uniformly matt; only the sanded high spots pick up a sheen */
    rough = 232 - Math.max(0, v) * 42;
    rd[i] = rd[i + 1] = rd[i + 2] = rough; rd[i + 3] = 255;
  }
  x.putImageData(im, 0, 0); hx.putImageData(hi, 0, 0); rx.putImageData(ri, 0, 0);

  /* damp rising from the floor line, and a few generations of patch repair */
  var g = x.createLinearGradient(0, H * .62, 0, H);
  g.addColorStop(0, 'rgba(96,74,48,0)');
  g.addColorStop(1, 'rgba(96,74,48,.34)');
  x.fillStyle = g; x.fillRect(0, H * .62, W, H * .38);
  var k, px2, py2, rw, rh;
  for (k = 0; k < 26; k++) {
    px2 = r0() * W; py2 = r0() * H; rw = 40 + r0() * 210; rh = 30 + r0() * 160;
    x.globalAlpha = .05 + r0() * .09;
    x.fillStyle = r0() > .5 ? rgb(232, 216, 188) : rgb(168, 143, 108);
    x.beginPath(); x.ellipse(px2, py2, rw, rh, r0() * PI, 0, TAU); x.fill();
  }
  x.globalAlpha = 1;
  return { map: toTex(c, 1, 1, true), normalMap: toTex(normalFromHeight(h, 1.5)), roughnessMap: toTex(r) };
}

/* — the floor: worn flagstones, swept a thousand times ------------------ */
function bakeFlagstone(seed) {
  var W = 1024, H = 1024;
  var c = cvs(W, H), x = c.getContext('2d');
  var h = cvs(W, H), hx = h.getContext('2d');
  var rc = cvs(W, H), rx = rc.getContext('2d');
  var r0 = rng(seed), n = noise2D(seed + 71);

  x.fillStyle = rgb(58, 48, 38); x.fillRect(0, 0, W, H);
  hx.fillStyle = '#3a3a3a'; hx.fillRect(0, 0, W, H);
  rx.fillStyle = '#c8c8c8'; rx.fillRect(0, 0, W, H);

  /* a running bond of hand-cut slabs, each with its own tone and wear */
  var rows = 5, gap = 5, i, j, y0, rowH = H / rows, off, cols, colW, x0;
  for (i = 0; i < rows; i++) {
    y0 = i * rowH;
    off = (i % 2) * (W / 7);
    cols = 3 + (i % 2);
    colW = W / cols;
    for (j = -1; j <= cols; j++) {
      x0 = j * colW + off;
      var t = .82 + r0() * .44;
      var jit = function () { return (r0() - .5) * 5; };
      var bx0 = x0 + gap + jit(), by0 = y0 + gap + jit();
      var bw = colW - gap * 2 + jit(), bh = rowH - gap * 2 + jit();
      x.fillStyle = rgb(96 * t, 82 * t, 66 * t);
      x.fillRect(bx0, by0, bw, bh);
      hx.fillStyle = rgb(150 + t * 60, 150 + t * 60, 150 + t * 60);
      hx.fillRect(bx0, by0, bw, bh);
      /* the centre of a slab is polished by feet; the edges stay matt */
      var pg = rx.createRadialGradient(bx0 + bw / 2, by0 + bh / 2, 2,
                                       bx0 + bw / 2, by0 + bh / 2, Math.max(bw, bh) * .62);
      var pv = 106 + ((r0() * 44) | 0);
      pg.addColorStop(0, rgb(pv, pv, pv));
      pg.addColorStop(1, 'rgb(206,206,206)');
      rx.fillStyle = pg; rx.fillRect(bx0, by0, bw, bh);
    }
  }
  /* grit, spills and the ghost of a hundred crates */
  var im = x.getImageData(0, 0, W, H), d = im.data, px, py, v, k;
  for (py = 0; py < H; py++) for (px = 0; px < W; px++) {
    k = (py * W + px) * 4;
    v = 1 + fbm(n, px / 60, py / 60, 3, 2.2, .5) * .30 + (r0() - .5) * .10;
    d[k] *= v; d[k + 1] *= v; d[k + 2] *= v;
  }
  x.putImageData(im, 0, 0);
  for (i = 0; i < 40; i++) {
    x.globalAlpha = .04 + r0() * .07;
    x.fillStyle = r0() > .45 ? rgb(40, 30, 20) : rgb(140, 118, 88);
    x.beginPath(); x.ellipse(r0() * W, r0() * H, 20 + r0() * 130, 16 + r0() * 90, r0() * PI, 0, TAU); x.fill();
  }
  x.globalAlpha = 1;
  return { map: toTex(c, 1, 1, true), normalMap: toTex(normalFromHeight(h, 2.6)), roughnessMap: toTex(rc) };
}

/* — timber: beams, trusses, crate slats, counter tops ------------------- */
function bakeWood(seed, opt) {
  opt = opt || {};
  var W = 512, H = 512;
  var c = cvs(W, H), x = c.getContext('2d');
  var h = cvs(W, H), hx = h.getContext('2d');
  var rc = cvs(W, H), rx = rc.getContext('2d');
  var r0 = rng(seed), base = opt.base || [126, 92, 58];
  var boards = opt.boards === undefined ? 6 : opt.boards;

  x.fillStyle = rgb(base[0] * .8, base[1] * .8, base[2] * .8); x.fillRect(0, 0, W, H);
  hx.fillStyle = '#8a8a8a'; hx.fillRect(0, 0, W, H);
  rx.fillStyle = '#b4b4b4'; rx.fillRect(0, 0, W, H);

  /* Boards run vertically in texture space; the geometry rotates the UVs
     where a beam needs its grain along its length. */
  var cuts = [0], i, k, y;
  if (boards > 0) {
    var widths = [], sum = 0;
    for (i = 0; i < boards; i++) { var w = .6 + r0() * .8; widths.push(w); sum += w; }
    var acc = 0;
    for (i = 0; i < boards; i++) { acc += widths[i] / sum; cuts.push(acc * W); }
  } else { cuts.push(W); }

  for (i = 0; i < cuts.length - 1; i++) {
    var x0 = cuts[i], bw = cuts[i + 1] - x0;
    var tone = .80 + r0() * .46;
    x.fillStyle = rgb(base[0] * tone, base[1] * tone, base[2] * tone);
    x.fillRect(x0, 0, bw, H);

    /* growth rings: a wandering pith line, then arcs stepping away from it */
    var pith = x0 + bw * (r0() * 2.6 - .8);
    var amp = bw * (.08 + r0() * .34), per = 1 + ((r0() * 2) | 0), ph = r0() * TAU;
    var gapR = 2.2 + r0() * 4.4;
    for (k = 0; k < 90; k++) {
      var dist = k * gapR;
      x.beginPath();
      for (y = 0; y <= H; y += 6) {
        var cx = pith + Math.sin(y / H * TAU * per + ph) * amp;
        var px1 = cx + dist, px2 = cx - dist;
        if (y === 0) x.moveTo(px1, y); else x.lineTo(px1, y);
      }
      x.strokeStyle = rgba(base[0] * .52, base[1] * .48, base[2] * .44, .16 + r0() * .16);
      x.lineWidth = .7 + r0() * 1.5;
      x.save(); x.beginPath(); x.rect(x0, 0, bw, H); x.clip(); x.stroke(); x.restore();
      x.beginPath();
      for (y = 0; y <= H; y += 6) {
        var cx2 = pith + Math.sin(y / H * TAU * per + ph) * amp;
        if (y === 0) x.moveTo(cx2 - dist, y); else x.lineTo(cx2 - dist, y);
      }
      x.save(); x.beginPath(); x.rect(x0, 0, bw, H); x.clip(); x.stroke(); x.restore();
      if (dist > bw * 1.6) break;
    }
    /* the shadow line in the joint between two boards */
    if (boards > 0) {
      x.fillStyle = 'rgba(24,16,10,.55)'; x.fillRect(x0, 0, 1.6, H);
      hx.fillStyle = '#2c2c2c'; hx.fillRect(x0, 0, 2.2, H);
      hx.fillStyle = rgb(120 + tone * 60, 120 + tone * 60, 120 + tone * 60);
      hx.fillRect(x0 + 2.2, 0, bw - 2.2, H);
    }
    /* knots, and the little halo of compressed grain around each */
    var knots = (r0() * 2.4) | 0;
    for (k = 0; k < knots; k++) {
      var kx = x0 + bw * (.2 + r0() * .6), ky = r0() * H, kr = 3 + r0() * 7;
      var kg = x.createRadialGradient(kx, ky, 1, kx, ky, kr * 2.4);
      kg.addColorStop(0, rgba(base[0] * .32, base[1] * .26, base[2] * .22, .95));
      kg.addColorStop(.45, rgba(base[0] * .5, base[1] * .42, base[2] * .34, .5));
      kg.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = kg; x.beginPath(); x.arc(kx, ky, kr * 2.4, 0, TAU); x.fill();
      hx.fillStyle = 'rgba(40,40,40,.7)'; hx.beginPath(); hx.arc(kx, ky, kr, 0, TAU); hx.fill();
    }
  }
  /* saw marks and long-use scuffing, applied across every board at once */
  for (k = 0; k < 220; k++) {
    x.globalAlpha = .04 + r0() * .09;
    x.strokeStyle = r0() > .5 ? 'rgb(30,20,12)' : 'rgb(200,176,138)';
    x.lineWidth = .5 + r0() * 1.2;
    var sy = r0() * H;
    x.beginPath(); x.moveTo(r0() * W, sy); x.lineTo(r0() * W, sy + (r0() - .5) * 26); x.stroke();
  }
  x.globalAlpha = 1;
  if (opt.wash) { x.fillStyle = opt.wash; x.fillRect(0, 0, W, H); }
  return { map: toTex(c, 1, 1, true), normalMap: toTex(normalFromHeight(h, 1.7)), roughnessMap: toTex(rc) };
}

/* — brick: the arcade piers and the lower course of the walls ----------- */
function bakeBrick(seed) {
  var W = 1024, H = 1024;
  var c = cvs(W, H), x = c.getContext('2d');
  var h = cvs(W, H), hx = h.getContext('2d');
  var r0 = rng(seed), n = noise2D(seed + 3);

  x.fillStyle = rgb(96, 84, 68); x.fillRect(0, 0, W, H);   /* mortar */
  hx.fillStyle = '#4a4a4a'; hx.fillRect(0, 0, W, H);

  var rows = 16, rowH = H / rows, perRow = 8, bw = W / perRow, i, j;
  for (i = 0; i < rows; i++) {
    var off = (i % 2) * bw * .5;
    for (j = -1; j <= perRow; j++) {
      var bx = j * bw + off + 2.5, by = i * rowH + 2.5;
      var w = bw - 5, hh = rowH - 5;
      var t = .74 + r0() * .5;
      var warm = r0() > .78;
      x.fillStyle = warm ? rgb(168 * t, 92 * t, 62 * t) : rgb(146 * t, 88 * t, 62 * t);
      x.fillRect(bx, by, w, hh);
      hx.fillStyle = rgb(160 + t * 70, 160 + t * 70, 160 + t * 70);
      hx.fillRect(bx, by, w, hh);
      /* face grain and the chipped arris on the older bricks */
      var k;
      for (k = 0; k < 14; k++) {
        x.globalAlpha = .05 + r0() * .10;
        x.fillStyle = r0() > .5 ? 'rgb(60,32,20)' : 'rgb(212,164,128)';
        x.fillRect(bx + r0() * w, by + r0() * hh, 2 + r0() * 16, 1 + r0() * 4);
      }
      x.globalAlpha = 1;
      if (r0() > .8) {
        x.fillStyle = rgb(104, 90, 72);
        x.fillRect(bx + r0() * w * .7, by, 4 + r0() * 10, 2 + r0() * 3);
      }
    }
  }
  var im = x.getImageData(0, 0, W, H), d = im.data, px, py, v, k2;
  for (py = 0; py < H; py++) for (px = 0; px < W; px++) {
    k2 = (py * W + px) * 4;
    v = 1 + fbm(n, px / 130, py / 130, 3, 2.1, .5) * .26;
    d[k2] *= v; d[k2 + 1] *= v; d[k2 + 2] *= v;
  }
  x.putImageData(im, 0, 0);
  return { map: toTex(c, 1, 1, true), normalMap: toTex(normalFromHeight(h, 2.4)), roughnessMap: null };
}

/* — woven fabric: hessian sacks, awnings, aprons ------------------------ */
/* opt.stencil / opt.sub print the mark every sack in every market on earth
   carries. Leave them out for plain cloth. */
function bakeFabric(seed, opt) {
  opt = opt || {};
  var W = 512, H = 512;
  var c = cvs(W, H), x = c.getContext('2d');
  var h = cvs(W, H), hx = h.getContext('2d');
  var r0 = rng(seed);
  x.fillStyle = rgb(172, 142, 96); x.fillRect(0, 0, W, H);
  hx.fillStyle = '#808080'; hx.fillRect(0, 0, W, H);
  var i, p;
  /* a plain weave, drawn thread by thread so the light catches the warp */
  for (i = 0; i < W; i += 4) {
    p = .82 + r0() * .38;
    x.fillStyle = rgba(150 * p, 122 * p, 80 * p, .8); x.fillRect(i, 0, 2.2, H);
    hx.fillStyle = rgba(190, 190, 190, .55); hx.fillRect(i, 0, 2.2, H);
  }
  for (i = 0; i < H; i += 4) {
    p = .82 + r0() * .38;
    x.fillStyle = rgba(186 * p, 156 * p, 108 * p, .55); x.fillRect(0, i, W, 2.2);
    hx.fillStyle = rgba(88, 88, 88, .5); hx.fillRect(0, i, W, 2.2);
  }
  for (i = 0; i < 60; i++) {
    x.globalAlpha = .05 + r0() * .09;
    x.fillStyle = r0() > .5 ? 'rgb(96,74,44)' : 'rgb(216,192,150)';
    x.beginPath(); x.ellipse(r0() * W, r0() * H, 12 + r0() * 60, 10 + r0() * 40, r0() * PI, 0, TAU); x.fill();
  }
  x.globalAlpha = 1;
  if (opt.stencil) {
    x.save();
    x.translate(W * .5, H * .5); x.rotate(-.03);
    x.globalAlpha = .30; x.fillStyle = 'rgb(52,44,30)';
    x.font = '600 42px ui-serif, Georgia, serif'; x.textAlign = 'center';
    x.fillText(opt.stencil, 0, -6);
    if (opt.sub) {
      x.font = '400 20px ui-sans-serif, system-ui, sans-serif';
      x.fillText(opt.sub, 0, 26);
    }
    x.restore();
  }
  return { map: toTex(c, 1, 1, true), normalMap: toTex(normalFromHeight(h, 1.5)), roughnessMap: null };
}

/* — a soft radial alpha, used for every glow, mote and shaft ------------ */
function bakeGlow(inner, mid) {
  var S = 128, c = cvs(S, S), x = c.getContext('2d');
  var g = x.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, 'rgba(255,255,255,' + (inner === undefined ? 1 : inner) + ')');
  g.addColorStop(.28, 'rgba(255,255,255,' + (mid === undefined ? .42 : mid) + ')');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g; x.fillRect(0, 0, S, S);
  var t = new THREE.CanvasTexture(c);
  t.needsUpdate = true;
  return t;
}
/* — a light shaft in cross-section: bright at the window, gone by the floor */
function bakeShaft() {
  var W = 64, H = 256, c = cvs(W, H), x = c.getContext('2d');
  var g = x.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(.10, 'rgba(255,255,255,.62)');
  g.addColorStop(.35, 'rgba(255,255,255,.42)');
  g.addColorStop(.78, 'rgba(255,255,255,.10)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  /* feather the two long edges so the shaft has no visible sides */
  var e = x.createLinearGradient(0, 0, W, 0);
  e.addColorStop(0, 'rgba(0,0,0,1)');
  e.addColorStop(.22, 'rgba(0,0,0,0)');
  e.addColorStop(.78, 'rgba(0,0,0,0)');
  e.addColorStop(1, 'rgba(0,0,0,1)');
  x.globalCompositeOperation = 'destination-out';
  x.fillStyle = e; x.fillRect(0, 0, W, H);
  x.globalCompositeOperation = 'source-over';
  var t = new THREE.CanvasTexture(c);
  t.needsUpdate = true;
  return t;
}
/* — paper labels for the jars and tins on the shelves ------------------- */
function bakeLabel(seed, hue, opt) {
  opt = opt || {};
  var brand = opt.brand || 'ETIKETT';
  var words = opt.words || ['ORIGINAL', 'RESERVE', 'No. 1', 'ESTD', 'SELECT'];
  var W = 256, H = 256, c = cvs(W, H), x = c.getContext('2d');
  var r0 = rng(seed);
  x.fillStyle = rgb(hue[0], hue[1], hue[2]); x.fillRect(0, 0, W, H);
  /* a band of label around the middle third of the jar */
  x.fillStyle = rgb(238, 226, 202); x.fillRect(0, H * .30, W, H * .42);
  x.strokeStyle = rgba(60, 44, 26, .5); x.lineWidth = 2;
  x.strokeRect(6, H * .30 + 8, W - 12, H * .42 - 16);
  x.fillStyle = 'rgb(56,42,24)';
  x.textAlign = 'center';
  x.font = '600 20px ui-serif, Georgia, serif';
  x.fillText(brand, W * .5, H * .47);
  x.font = '400 12px ui-sans-serif, system-ui, sans-serif';
  x.fillStyle = 'rgba(56,42,24,.72)';
  x.fillText(words[(r0() * words.length) | 0], W * .5, H * .565);
  x.fillStyle = 'rgb(194,84,44)';
  x.fillRect(W * .38, H * .60, W * .24, 3);
  return toTex(c, 1, 1, true);
}

/* ============================================== 3 · materials ============= */
function mat(rec, opt) {
  opt = opt || {};
  var m = new THREE.MeshStandardMaterial({
    map: rec ? rec.map : null,
    normalMap: rec ? rec.normalMap : null,
    roughnessMap: rec ? rec.roughnessMap : null,
    color: opt.color === undefined ? 0xffffff : opt.color,
    roughness: opt.roughness === undefined ? .92 : opt.roughness,
    metalness: opt.metalness === undefined ? 0 : opt.metalness,
    side: opt.side || THREE.FrontSide,
    transparent: !!opt.transparent,
    opacity: opt.opacity === undefined ? 1 : opt.opacity,
    emissive: opt.emissive === undefined ? 0x000000 : opt.emissive,
    emissiveIntensity: opt.emissiveIntensity === undefined ? 1 : opt.emissiveIntensity
  });
  if (m.normalMap && opt.normalScale) m.normalScale.set(opt.normalScale, opt.normalScale);
  return m;
}
/* Repeat is per-material, not per-texture, so a shared recipe can tile at one
   rate on a wall and another on a crate slat. Clone only the maps that exist. */
function tiled(rec, rx, ry) {
  var out = {};
  ['map', 'normalMap', 'roughnessMap'].forEach(function (k) {
    if (!rec[k]) { out[k] = null; return; }
    var t = rec[k].clone();
    t.needsUpdate = true;
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(rx, ry);
    t.anisotropy = K.maxAniso;
    out[k] = t;
  });
  return out;
}

/* ============================================== 4 · geometry and batching = */
/* A rich world is a thousand small objects. Submitted one mesh at a time that
   is a thousand draw calls a frame, which no phone will hold. Merge everything
   static into a handful of buffers — one per material — and the scene ends up
   with roughly twenty drawables in total. Only the lights, the glows, the
   shafts and the dust stay as objects of their own.

   Pass a colour to add() to write per-vertex colours, and every apple, jar and
   awning in the scene can share one vertex-coloured material instead of
   needing one material each. */
function Batch() { this.g = {}; }
Batch.prototype.add = function (geo, key, m4, color) {
  var g = geo.index ? geo.toNonIndexed() : geo.clone();
  g.applyMatrix4(m4);
  var n = g.attributes.position.count, i;
  if (color) {
    var col = new Float32Array(n * 3);
    for (i = 0; i < n; i++) { col[i * 3] = color[0]; col[i * 3 + 1] = color[1]; col[i * 3 + 2] = color[2]; }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  }
  (this.g[key] || (this.g[key] = [])).push(g);
};
Batch.prototype.build = function (materials, parent) {
  var key, list, i, j, total, needCol, geo, out = [];
  for (key in this.g) {
    list = this.g[key];
    total = 0; needCol = false;
    for (i = 0; i < list.length; i++) {
      total += list[i].attributes.position.count;
      if (list[i].attributes.color) needCol = true;
    }
    var pos = new Float32Array(total * 3), nrm = new Float32Array(total * 3);
    var uv = new Float32Array(total * 2), col = needCol ? new Float32Array(total * 3) : null;
    var off = 0;
    for (i = 0; i < list.length; i++) {
      var a = list[i].attributes, n = a.position.count;
      pos.set(a.position.array.subarray(0, n * 3), off * 3);
      if (a.normal) nrm.set(a.normal.array.subarray(0, n * 3), off * 3);
      if (a.uv) uv.set(a.uv.array.subarray(0, n * 2), off * 2);
      if (col) {
        if (a.color) col.set(a.color.array.subarray(0, n * 3), off * 3);
        else for (j = 0; j < n * 3; j++) col[off * 3 + j] = 1;
      }
      off += n;
      list[i].dispose();
    }
    geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    if (col) geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.computeBoundingSphere();
    var mesh = new THREE.Mesh(geo, materials[key]);
    mesh.castShadow = true; mesh.receiveShadow = true;
    mesh.frustumCulled = false;   /* one buffer spans the whole hall */
    parent.add(mesh);
    out.push(mesh);
  }
  this.g = {};
  return out;
};

/* geometry cache — a CylinderGeometry is built once per distinct shape */
var GEO = {};
function gBox(w, h, d) {
  var k = 'b' + w + '_' + h + '_' + d;
  return GEO[k] || (GEO[k] = new THREE.BoxGeometry(w, h, d));
}
function gCyl(rt, rb, h, s) {
  var k = 'c' + rt + '_' + rb + '_' + h + '_' + s;
  return GEO[k] || (GEO[k] = new THREE.CylinderGeometry(rt, rb, h, s));
}
function gSph(r, s) {
  var k = 's' + r + '_' + s;
  return GEO[k] || (GEO[k] = new THREE.SphereGeometry(r, s, Math.max(4, s >> 1)));
}
function gPlane(w, h) {
  var k = 'p' + w + '_' + h;
  return GEO[k] || (GEO[k] = new THREE.PlaneGeometry(w, h));
}

var _m4 = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
var _v3 = new THREE.Vector3(), _one = new THREE.Vector3(1, 1, 1);
function place(x, y, z, rx, ry, rz, sx, sy, sz) {
  _e.set(rx || 0, ry || 0, rz || 0);
  _q.setFromEuler(_e);
  _v3.set(x, y, z);
  _one.set(sx === undefined ? 1 : sx, sy === undefined ? 1 : sy, sz === undefined ? 1 : sz);
  return _m4.compose(_v3, _q, _one);
}

/* ---------------------------------------------------------------- exports */
K.PI = PI; K.TAU = TAU;
K.clamp = clamp; K.sat = sat; K.lerp = lerp; K.smoothstep = smoothstep; K.damp = damp;
K.rng = rng; K.noise2D = noise2D; K.fbm = fbm;
K.cvs = cvs; K.rgb = rgb; K.rgba = rgba; K.lin = lin;
K.normalFromHeight = normalFromHeight; K.toTex = toTex; K.recipe = recipe;
K.bakePlaster = bakePlaster; K.bakeFlagstone = bakeFlagstone; K.bakeWood = bakeWood;
K.bakeBrick = bakeBrick; K.bakeFabric = bakeFabric; K.bakeLabel = bakeLabel;
K.bakeGlow = bakeGlow; K.bakeShaft = bakeShaft;
K.mat = mat; K.tiled = tiled;
K.Batch = Batch; K.gBox = gBox; K.gCyl = gCyl; K.gSph = gSph; K.gPlane = gPlane;
K.place = place;

/* ============================================== 6 · the composer ========== */
/* Bright pass → two blurred octaves → ACES + sRGB, off an unclamped
   half-float buffer.

   Two things here are easy to get wrong and expensive to debug:

   1. Ping-pong across THREE distinct targets. Sampling a texture while its own
      framebuffer is bound is a feedback loop; some drivers silently drop the
      draw, others render garbage, and WebGL reports it only as a warning
      buried among thousands of identical ones.
   2. Threshold in HDR luminance, not in display luminance. With the scene
      rendered unclamped, a threshold near 1.0 means "brighter than white",
      which is exactly the set of things that should bloom. Thresholds below 1
      bloom the whole image into fog. */
K.Composer = function (renderer, opt) {
  opt = opt || {};
  var caps = renderer.capabilities;
  var half = caps.isWebGL2 || renderer.extensions.has('OES_texture_half_float');
  var type = half ? THREE.HalfFloatType : THREE.UnsignedByteType;
  var C = { on: false, renderer: renderer };

  function quad(fragment, uniforms) {
    var m = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: 'varying vec2 vUv;\n' +
        'void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: fragment, depthTest: false, depthWrite: false
    });
    var mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m);
    mesh.frustumCulled = false;
    var s = new THREE.Scene(); s.add(mesh);
    return { scene: s, mat: m };
  }

  var w = 2, h = 2;
  var o = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat, type: type };
  C.rtScene = new THREE.WebGLRenderTarget(w, h, o);
  C.rtScene.depthBuffer = true;
  /* Long straight edges — beams, mullions, horizons — alias badly without
     this, and there is no MSAA on a render target outside WebGL2. */
  var tier = (renderer.userData && renderer.userData.quality) || 1;
  if (caps.isWebGL2) C.rtScene.samples = tier < .8 ? 2 : 4;
  C.rtA = new THREE.WebGLRenderTarget(w, h, o);
  C.rtB = new THREE.WebGLRenderTarget(w, h, o);
  C.rtC = new THREE.WebGLRenderTarget(w, h, o);
  C.rtD = new THREE.WebGLRenderTarget(w, h, o);
  [C.rtScene, C.rtA, C.rtB, C.rtC, C.rtD].forEach(function (rt) {
    rt.texture.encoding = THREE.LinearEncoding;
    rt.texture.generateMipmaps = false;
  });

  C.bright = quad([
    'uniform sampler2D tIn; uniform float uThresh; uniform float uKnee;',
    'varying vec2 vUv;',
    'void main(){',
    '  vec3 c = texture2D(tIn, vUv).rgb;',
    '  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));',
    '  float f = smoothstep(uThresh, uThresh + uKnee, l);',
    '  gl_FragColor = vec4(c * f, 1.0);',
    '}'
  ].join('\n'), {
    tIn: { value: C.rtScene.texture },
    uThresh: { value: K.qn('bthr', opt.threshold === undefined ? 1.02 : opt.threshold) },
    uKnee: { value: opt.knee === undefined ? .85 : opt.knee }
  });

  /* nine taps in five fetches — the standard linear-sampling gaussian */
  C.blur = quad([
    'uniform sampler2D tIn; uniform vec2 uDir;',
    'varying vec2 vUv;',
    'void main(){',
    '  vec4 s = texture2D(tIn, vUv) * 0.2270270270;',
    '  s += texture2D(tIn, vUv + uDir * 1.3846153846) * 0.3162162162;',
    '  s += texture2D(tIn, vUv - uDir * 1.3846153846) * 0.3162162162;',
    '  s += texture2D(tIn, vUv + uDir * 3.2307692308) * 0.0702702703;',
    '  s += texture2D(tIn, vUv - uDir * 3.2307692308) * 0.0702702703;',
    '  gl_FragColor = s;',
    '}'
  ].join('\n'), { tIn: { value: null }, uDir: { value: new THREE.Vector2() } });

  C.comp = quad([
    'uniform sampler2D tScene; uniform sampler2D tBloomA; uniform sampler2D tBloomB;',
    'uniform float uBloom; uniform float uExp; uniform float uSat; uniform float uLift;',
    'uniform vec3 uWarm; uniform float uWarmMix; uniform float uFade;',
    'varying vec2 vUv;',
    /* Narkowicz\'s ACES fit: cheap, and it rolls a highlight off to white
       instead of clipping it to a flat coloured plate. */
    'vec3 aces(vec3 x){',
    '  return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);',
    '}',
    'vec3 toSRGB(vec3 c){',
    '  return mix(c * 12.92, 1.055 * pow(max(c, vec3(1e-5)), vec3(1.0/2.4)) - 0.055,',
    '             step(vec3(0.0031308), c));',
    '}',
    'void main(){',
    '  vec3 c = texture2D(tScene, vUv).rgb;',
    '  vec3 b = texture2D(tBloomA, vUv).rgb * 0.62 + texture2D(tBloomB, vUv).rgb * 0.38;',
    '  c += b * uBloom;',
    '  c *= uExp;',
    /* the grade: one push toward the hour of day, and a shadow lift so the
       darks are a colour rather than a hole */
    '  c = mix(c, c * uWarm, uWarmMix);',
    '  c += uWarm * uLift * (1.0 - smoothstep(0.0, 0.25, dot(c, vec3(0.33))));',
    '  c = aces(c);',
    '  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));',
    '  c = mix(vec3(l), c, uSat);',
    '  gl_FragColor = vec4(toSRGB(c) * uFade, 1.0);',
    '}'
  ].join('\n'), {
    tScene:  { value: C.rtScene.texture },
    tBloomA: { value: C.rtA.texture },
    tBloomB: { value: C.rtD.texture },
    uBloom:  { value: K.qn('bloom', opt.bloom === undefined ? .30 : opt.bloom) },
    uExp:    { value: K.qn('exp', opt.exposure === undefined ? .80 : opt.exposure) },
    uSat:    { value: opt.saturation === undefined ? 1.04 : opt.saturation },
    uWarm:   { value: new THREE.Vector3().fromArray(opt.grade || [1.06, .99, .88]) },
    uWarmMix:{ value: opt.gradeMix === undefined ? .55 : opt.gradeMix },
    /* How far the darkest pixels are lifted off black. Generous in a dark
       scene, where it keeps shadows a colour rather than a hole; poison in a
       bright one, where it puts a grey floor under every dark object and
       turns a black cabinet against white plaster into a pale slab. */
    uLift:   { value: opt.lift === undefined ? .014 : opt.lift },
    uFade:   { value: 1 }
  });

  C.cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  C.on = true;

  C.setSize = function (cw, ch) {
    var p = renderer.getPixelRatio();
    var W = Math.max(2, Math.floor(cw * p)), H = Math.max(2, Math.floor(ch * p));
    var bw = Math.max(2, W >> 1), bh = Math.max(2, H >> 1);
    C.rtScene.setSize(W, H);
    C.rtA.setSize(bw, bh); C.rtB.setSize(bw, bh);
    C.rtC.setSize(Math.max(2, bw >> 1), Math.max(2, bh >> 1));
    C.rtD.setSize(Math.max(2, bw >> 1), Math.max(2, bh >> 1));
  };
  function pass(q, target) {
    renderer.setRenderTarget(target || null);
    renderer.render(q.scene, C.cam);
  }
  /* src, mid and dst must all be different targets — see the note above. */
  function blur2(srcTex, mid, dst, tx, ty) {
    C.blur.mat.uniforms.tIn.value = srcTex;
    C.blur.mat.uniforms.uDir.value.set(tx, 0);
    pass(C.blur, mid);
    C.blur.mat.uniforms.tIn.value = mid.texture;
    C.blur.mat.uniforms.uDir.value.set(0, ty);
    pass(C.blur, dst);
  }
  C.render = function (scene, camera) {
    if (!C.on) { renderer.setRenderTarget(null); renderer.render(scene, camera); return; }
    renderer.setRenderTarget(C.rtScene);
    renderer.clear();
    renderer.render(scene, camera);
    pass(C.bright, C.rtA);
    blur2(C.rtA.texture, C.rtB, C.rtA, 1 / C.rtA.width, 1 / C.rtA.height);   /* the halo */
    blur2(C.rtA.texture, C.rtC, C.rtD, 2 / C.rtC.width, 2 / C.rtC.height);   /* the tail */
    pass(C.comp, null);
  };
  return C;
};
/* If the composer throws — an old driver, no float targets — hand tone mapping
   and the transfer function back to the renderer and draw straight to the
   canvas. The scene loses its bloom and keeps everything else. */
K.composerFallback = function (renderer, err) {
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = K.qn('exp', 1.04);
  if (global.console) global.console.warn('[scene-kit] bloom disabled:', err && err.message);
  return { on: false, setSize: function () {},
           render: function (s, c) { renderer.setRenderTarget(null); renderer.render(s, c); } };
};

/* ============================================== 7 · the camera rig ======== */
/* A handful of composed shots, interpolated along a Catmull-Rom, driven by a
   scroll progress that is already expressed in chapter units (see
   page.progress). Each waypoint is a position, a look-at target and a field
   of view — three numbers to change a shot, which is what makes tuning a
   sequence feasible at all.

   Compose every waypoint for a WIDE frame and let fitAspect handle the rest:
   on a tall viewport it steps the rig back along its own view axis and opens
   the lens, rather than letting the sides of the world fall out of frame. */
K.Rig = function (waypoints, opt) {
  opt = opt || {};
  var R = {
    cam: waypoints, prog: 0, smooth: 0,
    mx: 0, my: 0, intro: 0,
    parallax: opt.parallax === undefined ? .55 : opt.parallax,
    introDolly: opt.introDolly === undefined ? 4.2 : opt.introDolly
  };
  var V = function (c) { return new THREE.Vector3(c[0], c[1], c[2]); };
  R.curveP = new THREE.CatmullRomCurve3(waypoints.map(function (c) { return V(c.p); }),
                                        false, 'catmullrom', .40);
  R.curveT = new THREE.CatmullRomCurve3(waypoints.map(function (c) { return V(c.t); }),
                                        false, 'catmullrom', .40);
  var _p = new THREE.Vector3(), _t = new THREE.Vector3(), _d = new THREE.Vector3();

  R.aspectFix = function (aspect) { return K.clamp((1.58 - aspect) / 1.0, 0, 1); };
  R.apply = function (camera, aspect) {
    var N = waypoints.length - 1;
    var u = K.clamp(R.smooth / N, 0, 1);
    R.curveP.getPoint(u, _p);
    R.curveT.getPoint(u, _t);
    var i = K.clamp(Math.floor(R.smooth), 0, N - 1), f = K.clamp(R.smooth - i, 0, 1);
    var fov = K.lerp(waypoints[i].fov, waypoints[i + 1].fov, f);

    var nf = R.aspectFix(aspect);
    if (nf > 0) {
      _d.subVectors(_p, _t).normalize();
      _p.addScaledVector(_d, nf * 5.4);
      _p.y += nf * .55;
      fov *= (1 + nf * .30);
    }
    /* the opening dolly: a longer lens easing in from further back. Make sure
       the world has room behind the first waypoint for this — the classic
       symptom of forgetting is a first frame rendered from inside a wall. */
    var io = 1 - R.intro;
    _p.z += io * R.introDolly; _p.y += io * .5; fov += io * 7;

    /* a hand-held drift from the pointer, damped away as the journey deepens */
    var par = 1 - K.smoothstep(0, 1.8, R.smooth) * .5;
    _p.x += R.mx * .55 * par; _p.y += R.my * .30 * par;
    _t.x -= R.mx * .18 * par; _t.y -= R.my * .10 * par;

    camera.position.copy(_p);
    camera.lookAt(_t);
    if (Math.abs(camera.fov - fov) > 1e-4) { camera.fov = fov; camera.updateProjectionMatrix(); }
  };
  /* Call each frame with the page's progress. dt is clamped so a stalled tab
     cannot teleport the camera, which also means the intro is measured in
     rendered frames on a very slow device — keep introDolly small enough that
     a slow first second is still a legal camera position. */
  R.step = function (progress, pointer, dt) {
    R.prog = progress;
    R.smooth = K.REDUCE ? progress : K.damp(R.smooth, progress, 3.4, dt);
    R.intro = Math.min(1, R.intro + dt / 2.2);
    if (pointer) {
      R.mx = K.damp(R.mx, pointer.x, 2.6, dt);
      R.my = K.damp(R.my, pointer.y, 2.6, dt);
    }
  };
  /* How square-on the camera is to a direction, 0..1. Fade crossed-plane light
     shafts out with this: looking down their own axis stacks every plane in
     the scene onto the same few pixels and draws a white star. */
  var _f = new THREE.Vector3();
  R.axisFade = function (camera, dir, lo, hi) {
    camera.getWorldDirection(_f);
    return 1 - K.smoothstep(lo === undefined ? .30 : lo,
                            hi === undefined ? .80 : hi, Math.abs(_f.dot(dir)));
  };
  return R;
};

/* ============================================== 5 · the renderer =========== */
/* Deliberately HDR-first: no tone mapping and no transfer function happen
   here. The scene is rendered linear and unclamped into a half-float buffer so
   a window, a doorway or a filament can sit at three or six times white, which
   is the only way they bloom like light instead of like pale rectangles. ACES
   and sRGB are applied once, at the end, by the Composer. If the composer
   cannot come up, hand both back to the renderer — see Composer.fallback. */
K.createRenderer = function (canvas, opt) {
  opt = opt || {};
  var r = new THREE.WebGLRenderer({
    canvas: canvas, antialias: false, alpha: false,
    powerPreference: 'high-performance', stencil: false, depth: true
  });
  var w = opt.width || innerWidth, h = opt.height || innerHeight;
  var dpr = Math.min(devicePixelRatio || 1, opt.maxDpr || 2);
  /* The ceiling is the pixel count, not the device class. A phone renders the
     same scene at the same field of view into a third of the pixels, and a
     4K desktop is far harder work than a laptop retina panel. */
  var budget = w * h * dpr * dpr;
  /* WebGLRenderer has no userData of its own; give it one so the quality tier
     travels with the renderer instead of living in a module-level global. */
  r.userData = r.userData || {};
  r.userData.quality = budget > 4.4e6 ? .72 : (budget > 2.2e6 ? .88 : 1);
  r.setPixelRatio(dpr * r.userData.quality);
  r.setSize(w, h, false);
  r.outputEncoding = THREE.LinearEncoding;
  r.toneMapping = THREE.NoToneMapping;
  r.shadowMap.enabled = opt.shadows !== false;
  r.shadowMap.type = THREE.PCFSoftShadowMap;
  /* A world made of static geometry has static shadows. Draw the map once at
     the end of the build and freeze it: this single line is usually the
     difference between 25fps and 60 on integrated graphics. */
  r.shadowMap.autoUpdate = false;
  r.setClearColor(opt.clear === undefined ? 0x000000 : opt.clear, 1);
  K.maxAniso = Math.min(8, r.capabilities.getMaxAnisotropy());
  return r;
};

/* ================================================ 8 · air ================== */
/* Dust is what makes a shaft of light legible as light. Two populations: a
   thin haze through the whole volume, and a dense band seeded inside the
   shafts themselves, where dust is actually visible. Drift happens in the
   vertex shader so the CPU never touches the buffer after boot.

   `seed(i, r)` is yours to write: return { x, y, z, r, g, b, size } for mote i,
   with `size` in WORLD UNITS — a couple of centimetres. Passing a pixel count
   here is the classic mistake: gl_PointSize is divided by view depth, so a
   nine-pixel mote two metres from the lens becomes a nine-hundred-pixel white
   sheet across the frame. */
K.dust = function (count, seed, glowTex, viewportH) {
  var pos = new Float32Array(count * 3), sd = new Float32Array(count);
  var size = new Float32Array(count), col = new Float32Array(count * 3);
  var r = K.rng(4242), i, m;
  for (i = 0; i < count; i++) {
    m = seed(i, r);
    pos[i * 3] = m.x; pos[i * 3 + 1] = m.y; pos[i * 3 + 2] = m.z;
    col[i * 3] = m.r; col[i * 3 + 1] = m.g; col[i * 3 + 2] = m.b;
    size[i] = m.size; sd[i] = r();
  }
  var g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aSeed', new THREE.BufferAttribute(sd, 1));
  g.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
  g.setAttribute('aCol', new THREE.BufferAttribute(col, 3));
  var mat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 }, uPix: { value: (viewportH || innerHeight) * .5 },
      uMap: { value: glowTex }, uFade: { value: 1 }
    },
    vertexShader: [
      'attribute float aSeed; attribute float aSize; attribute vec3 aCol;',
      'uniform float uTime; uniform float uPix;',
      'varying vec3 vCol; varying float vA;',
      'void main(){',
      '  float s = aSeed * 61.7;',
      '  vec3 p = position;',
      '  p.x += sin(uTime * 0.11 + s) * 0.42;',
      '  p.y += sin(uTime * 0.07 + s * 1.7) * 0.30 - fract(uTime * 0.012 + aSeed) * 0.9 + 0.45;',
      '  p.z += cos(uTime * 0.09 + s * 2.3) * 0.42;',
      '  vec4 mv = modelViewMatrix * vec4(p, 1.0);',
      '  gl_Position = projectionMatrix * mv;',
      '  gl_PointSize = clamp(aSize * uPix / max(0.6, -mv.z), 0.6, 22.0);',
      '  vCol = aCol;',
      '  vA = 0.42 + 0.58 * abs(sin(uTime * 0.9 + s));',
      '}'
    ].join('\n'),
    fragmentShader: [
      'uniform sampler2D uMap; uniform float uFade;',
      'varying vec3 vCol; varying float vA;',
      'void main(){',
      '  vec4 t = texture2D(uMap, gl_PointCoord);',
      '  gl_FragColor = vec4(vCol * vA * uFade, t.a * vA * uFade);',
      '}'
    ].join('\n'),
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending
  });
  var pts = new THREE.Points(g, mat);
  pts.frustumCulled = false;
  pts.renderOrder = 8;
  return pts;
};

/* A shaft of light through an opening, as three planes crossed about the
   shaft's own axis. Three crossed planes read as a volume from every angle a
   camera path realistically takes, and cost nothing; a real volumetric pass
   costs more than the rest of the world put together, and a single billboard
   gives itself away the moment the camera moves off it.

   Push the results into a Batch keyed to one additive material, then fade the
   whole set out as the camera turns to look ALONG the shafts: down their own
   axis every crossed plane in the scene stacks onto the same few pixels and
   draws a white star. See Rig.axisFade. */
K.shaftVolume = function (batch, key, origin, dir, length, width) {
  var o = new THREE.Object3D(), axis = new THREE.Vector3(0, 1, 0);
  var back = dir.clone().negate();
  var pos = origin.clone().addScaledVector(dir, length / 2);
  for (var i = 0; i < 3; i++) {
    o.position.copy(pos);
    o.quaternion.setFromUnitVectors(axis, back);
    o.rotateOnAxis(axis, i * Math.PI / 3);
    o.updateMatrix();
    batch.add(K.gPlane(width, length), key, o.matrix);
  }
};

/* ============================================== 9 · the page ============== */
/* The DOM half. It owns the reading — reveals, the chapter rail, the nav, the
   drawer, the cursor, the scrim handover, the preloader and the failure path —
   and knows nothing about the scene. Your project file owns the world and the
   frame loop and asks this object where the scroll currently is.

   The split matters: the reading is the product and the world is the
   atmosphere, so the reading has to keep working when the world does not.
   Everything here runs before WebGL is even probed. */
K.page = function (root, opt) {
  opt = opt || {};
  var ROOT = typeof root === 'string' ? document.querySelector(root) : root;
  if (!ROOT) throw new Error('SceneKit.page: root not found');
  var $  = function (s) { return ROOT.querySelector(s); };
  var $$ = function (s) { return [].slice.call(ROOT.querySelectorAll(s)); };

  var P = { root: ROOT, sections: [], active: -1, anchors: [], maxScroll: 1 };
  var navEl, preFill, prePct, preEl, drawerEl, burgerEl, cursorEl;
  var railEls = [], chipEls = [];
  var SCRIMS = ['left', 'right', 'wide', 'center', 'foot'];

  /* ---- grain ---------------------------------------------------------- */
  /* A still plate of noise, generated once. Keep it on soft-light, not
     overlay: overlay amplifies mid-tone contrast and traces a ghost of every
     letterform underneath the plate on a dark page. */
  function paintGrain() {
    var el = $('.cs-grain');
    if (!el) return;
    var S = 250, c = K.cvs(S, S), x = c.getContext('2d');
    var im = x.createImageData(S, S), d = im.data, r = K.rng(88), i, v;
    for (i = 0; i < S * S; i++) {
      v = 128 + (r() - .5) * 40;
      d[i * 4] = d[i * 4 + 1] = d[i * 4 + 2] = v; d[i * 4 + 3] = 255;
    }
    x.putImageData(im, 0, 0);
    el.style.backgroundImage = 'url(' + c.toDataURL('image/png') + ')';
  }

  /* ---- word masks ------------------------------------------------------ */
  /* Headings are dealt a word at a time. Each word rides up out of its own
     overflow:hidden mask — which is why the heading's shadow has to be a
     drop-shadow on the element rather than a text-shadow on the words: a
     blurred text-shadow gets clipped by the mask and draws a visible grey
     rectangle around every word. chapters.css already does this; if you
     restyle headings, keep it. */
  function splitWords() {
    $$('[data-cs-words]').forEach(function (el) {
      if (el.dataset.csSplit) return;
      el.dataset.csSplit = '1';
      el.classList.add('cs-words');
      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach(function (w, i) {
        var mask = document.createElement('span');
        mask.className = 'cs-word-mask';
        var inner = document.createElement('span');
        inner.className = 'cs-word';
        inner.style.setProperty('--cs-word-delay', (i * 68) + 'ms');
        inner.textContent = w;
        mask.appendChild(inner);
        el.appendChild(mask);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
    });
  }

  /* ---- the wordmark --------------------------------------------------- */
  /* A brand name is however many letters it is, in whatever face the host
     happens to load. Sizing it in vw is a guess that breaks on the first long
     name or unexpected font; measuring once at a known size and scaling from
     the result lands it flush to both gutters at every width. */
  function fitWordmark() {
    var el = $('.cs-wordmark');
    if (!el) return;
    var avail = el.clientWidth;
    if (!avail) return;
    el.style.fontSize = '100px';
    var w = el.scrollWidth;
    if (!w) { el.style.fontSize = ''; return; }
    el.style.fontSize = K.clamp(100 * (avail / w) * .995, 24, 280).toFixed(1) + 'px';
  }

  /* ---- reveals --------------------------------------------------------- */
  function initReveal() {
    var targets = $$('[data-cs-rv], [data-cs-words]');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (t) { t.classList.add('cs-in'); });
      return;
    }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('cs-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: .1 });
    targets.forEach(function (t) { io.observe(t); });
  }

  /* ---- measurement ----------------------------------------------------- */
  function vpH() { return Math.max(1, innerHeight); }
  P.measure = function () {
    var doc = document.documentElement;
    P.maxScroll = Math.max(1, doc.scrollHeight - vpH());
    var rootTop = ROOT.getBoundingClientRect().top + (scrollY || doc.scrollTop);
    P.anchors = P.sections.map(function (el, i) {
      if (i === 0) return Math.max(0, rootTop);
      if (i === P.sections.length - 1) return P.maxScroll;
      var top = el.getBoundingClientRect().top + (scrollY || doc.scrollTop);
      return K.clamp(top + el.offsetHeight * .5 - vpH() * .5, 0, P.maxScroll);
    });
    for (var i = 1; i < P.anchors.length; i++) {
      P.anchors[i] = Math.max(P.anchors[i], P.anchors[i - 1] + 1);
    }
    /* Width comes from --cs-vw, written here, not from right:0 on the nav. A
       fixed element resolves its inset against the initial containing block,
       and a host page that lets the document overflow horizontally makes that
       block wider than the screen — which carries the burger off the edge. */
    ROOT.style.setProperty('--cs-vw', doc.clientWidth + 'px');
    fitWordmark();
  };
  /* Chapter progress as a real number: 2.4 means "40% of the way from the
     third waypoint to the fourth". The rig reads exactly this. */
  P.progress = function () {
    var y = scrollY || document.documentElement.scrollTop;
    if (!P.anchors.length) return 0;
    if (y <= P.anchors[0]) return 0;
    for (var i = 0; i < P.anchors.length - 1; i++) {
      if (y <= P.anchors[i + 1]) {
        return i + (y - P.anchors[i]) / (P.anchors[i + 1] - P.anchors[i]);
      }
    }
    return P.anchors.length - 1;
  };

  /* ---- the active chapter ---------------------------------------------- */
  P.setActive = function (i) {
    if (i === P.active) return;
    P.active = i;
    railEls.forEach(function (b, k) { b.classList.toggle('cs-on', k === i); });
    chipEls.forEach(function (c) {
      c.classList.toggle('cs-on', parseInt(c.getAttribute('data-cs-chip'), 10) === i);
    });
    var sec = P.sections[i], id = sec ? sec.id : '';
    $$('.cs-nav-links a').forEach(function (a) {
      a.classList.toggle('cs-on', !!id && a.getAttribute('href') === '#' + id);
    });
    /* Hand the fixed scrim plate over to the incoming chapter. Doing this on
       one fixed layer rather than a ::before per section is what keeps a hard
       horizontal seam from being drawn across the world at every boundary. */
    var want = (sec && sec.getAttribute('data-cs-scrim')) || 'left';
    SCRIMS.forEach(function (k) { ROOT.classList.toggle('cs-scrim-' + k, k === want); });
    if (opt.onChapter) opt.onChapter(i, sec);
  };

  /* ---- anchors, rail, producers, drawer, cursor, nav -------------------- */
  function scrollToId(id) {
    var el = id && ROOT.querySelector(id);
    if (!el) return false;
    var y = el.getBoundingClientRect().top + (scrollY || document.documentElement.scrollTop);
    scrollTo({ top: Math.max(0, y - 10), behavior: K.REDUCE ? 'auto' : 'smooth' });
    return true;
  }
  function closeDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.remove('cs-open');
    drawerEl.setAttribute('aria-hidden', 'true');
    if (burgerEl) { burgerEl.classList.remove('cs-x'); burgerEl.setAttribute('aria-expanded', 'false'); }
    document.documentElement.classList.remove('cs-locked');
  }
  function initChrome() {
    ROOT.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"], [data-cs-goto]');
      if (!a) return;
      var id = a.getAttribute('data-cs-goto') || a.getAttribute('href');
      if (!id || id === '#') return;
      if (scrollToId(id)) { e.preventDefault(); closeDrawer(); }
    });
    chipEls.forEach(function (c) {
      c.addEventListener('click', function () { scrollToId(c.getAttribute('data-cs-target')); });
    });
    var rail = $('.cs-rail');
    if (rail) P.sections.forEach(function () {
      var b = document.createElement('i');
      b.setAttribute('aria-hidden', 'true');
      rail.appendChild(b); railEls.push(b);
    });
    if (burgerEl) burgerEl.addEventListener('click', function () {
      if (drawerEl && drawerEl.classList.contains('cs-open')) closeDrawer();
      else if (drawerEl) {
        drawerEl.classList.add('cs-open');
        drawerEl.setAttribute('aria-hidden', 'false');
        burgerEl.classList.add('cs-x');
        burgerEl.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('cs-locked');
      }
    });
    addEventListener('keydown', function (e) { if (e.key === 'Escape') closeDrawer(); });

    /* one open at a time, and reachable from the keyboard */
    $$('.cs-acc').forEach(function (p) {
      var head = p.querySelector('.cs-acc-head');
      if (!head) return;
      head.setAttribute('tabindex', '0');
      head.setAttribute('role', 'button');
      head.setAttribute('aria-expanded', 'false');
      var toggle = function () {
        var open = p.classList.contains('cs-open');
        $$('.cs-acc').forEach(function (o) {
          o.classList.remove('cs-open');
          var h = o.querySelector('.cs-acc-head');
          if (h) h.setAttribute('aria-expanded', 'false');
        });
        if (!open) { p.classList.add('cs-open'); head.setAttribute('aria-expanded', 'true'); }
      };
      head.addEventListener('click', toggle);
      head.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });
    var first = $('.cs-acc');
    if (first) {
      first.classList.add('cs-open');
      var h0 = first.querySelector('.cs-acc-head');
      if (h0) h0.setAttribute('aria-expanded', 'true');
    }

    var last = 0;
    var onScroll = function () {
      var y = scrollY || document.documentElement.scrollTop;
      if (navEl) {
        navEl.classList.toggle('cs-solid', y > 40);
        navEl.classList.toggle('cs-hidden', y > last && y > 200 &&
          !(drawerEl && drawerEl.classList.contains('cs-open')));
      }
      last = y;
    };
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    if (K.FINE && cursorEl && !K.REDUCE) {
      ROOT.classList.add('cs-cursor-on');
      var tx = 0, ty = 0, cx = 0, cy = 0, shown = false;
      addEventListener('pointermove', function (e) {
        tx = e.clientX; ty = e.clientY;
        if (!shown) { shown = true; cx = tx; cy = ty; cursorEl.classList.add('cs-vis'); }
        var over = e.target && e.target.closest &&
                   e.target.closest('a, button, .cs-chip, .cs-acc-head, .cs-card');
        cursorEl.classList.toggle('cs-hot', !!over);
      }, { passive: true });
      addEventListener('pointerleave', function () { cursorEl.classList.remove('cs-vis'); });
      (function loop() {
        cx += (tx - cx) * .22; cy += (ty - cy) * .22;
        cursorEl.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0)' +
          (cursorEl.classList.contains('cs-hot') ? ' scale(1.55)' : '');
        requestAnimationFrame(loop);
      })();
    }
  }

  /* ---- pointer drift, progress, states --------------------------------- */
  P.pointer = { x: 0, y: 0 };
  function initPointer() {
    if (K.COARSE || K.REDUCE) return;
    addEventListener('pointermove', function (e) {
      P.pointer.x = (e.clientX / Math.max(1, ROOT.clientWidth) - .5) * 2;
      P.pointer.y = (e.clientY / vpH() - .5) * 2;
    }, { passive: true });
  }
  P.setProgress = function (p) {
    if (preFill) preFill.style.right = (100 - Math.round(p * 100)) + '%';
    if (prePct) prePct.textContent = String(Math.round(p * 100)).padStart(3, '0');
  };
  P.ready = function () {
    ROOT.classList.add('cs-live');
    if (preEl) preEl.classList.add('cs-done');
    setTimeout(P.measure, 400);
  };
  /* The world is optional; the reading is not. Any failure — no WebGL, no
     float targets, a driver quirk, an exception in the texture kitchen —
     lands here, and the page keeps every word, link and price on screen over
     a painted backdrop instead of a live one. */
  P.fail = function (why) {
    ROOT.classList.remove('cs-live');
    ROOT.classList.add('cs-flat', 'cs-nojs');
    if (preEl) preEl.classList.add('cs-done');
    if (why && global.console) console.warn('[scene-kit] scene disabled:', why);
  };
  /* Probe before building. Note that three tries 'experimental-webgl' too, so
     a test that only blocks 'webgl' and 'webgl2' will not exercise this path. */
  P.hasWebGL = function () {
    if (!THREE) return false;
    try {
      var c = document.createElement('canvas');
      return !!(c.getContext('webgl2') || c.getContext('webgl') ||
                c.getContext('experimental-webgl'));
    } catch (e) { return false; }
  };

  /* ---- boot ------------------------------------------------------------ */
  ROOT.classList.remove('cs-nojs');
  P.sections = $$('[data-cs-cam]');
  navEl    = $('.cs-nav');
  preEl    = $('.cs-pre');
  preFill  = $('.cs-pre-bar i');
  prePct   = $('.cs-pre-pct');
  chipEls  = $$('.cs-chip');
  cursorEl = $('.cs-cursor');
  drawerEl = $('.cs-drawer');
  burgerEl = $('.cs-burger');
  paintGrain();
  splitWords();
  initReveal();
  initChrome();
  initPointer();
  P.measure();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitWordmark);
  addEventListener('resize', P.measure, { passive: true });
  addEventListener('orientationchange', function () { setTimeout(P.measure, 250); });
  addEventListener('load', P.measure);
  /* A section grows when a lazy image lands; the anchors have to follow. */
  if (global.ResizeObserver) new ResizeObserver(P.measure).observe(ROOT);
  return P;
};

global.SceneKit = K;
})(window);
